import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Popover, Skeleton, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  AggregateScoreType,
  ScoreRating,
  useGetAggregateScoresCountQuery,
} from "~/operations";
import { useMetrics } from "~/hooks";
import { Metrics } from "~/lib/arrow";
import { BoomStatCard } from "~/pages/platform/policy/components/BoomStats/BoomStatCard";
import DashboardSkeleton from "~/pages/organization/dashboard/DashboardSkeleton";
import type { SpaceEdge } from "./types";

export type OrgBoomStatsProps = {
  organizationMrn: string;
  spaceEdges: SpaceEdge[];
  spacesLoading: boolean;
  totalSpaces: number;
};

const baseFilter = {
  scoreType: AggregateScoreType.Vulnerability,
  minRiskValue: 1,
};

type SpaceInfo = { id: string; name: string; mrn: string };

function useCount(
  entityMrn: string,
  filter: Record<string, unknown>,
  skip = false,
) {
  const { data, loading } = useGetAggregateScoresCountQuery({
    variables: { entityMrn, filter: { ...baseFilter, ...filter } },
    skip,
  });
  const count =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores.totalCount
      : 0;
  return { count, loading };
}

// Per-space count row — each instance manages its own query
function SpaceCountRow({
  space,
  filter,
  href,
  skip,
}: {
  space: SpaceInfo;
  filter: Record<string, unknown>;
  href: string;
  skip?: boolean;
}) {
  const { data, loading } = useGetAggregateScoresCountQuery({
    variables: {
      entityMrn: space.mrn,
      filter: { ...baseFilter, ...filter },
    },
    skip,
  });
  const count =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores.totalCount
      : 0;

  if (loading) return <Skeleton variant="text" sx={{ fontSize: 13 }} />;
  if (count === 0) return null;

  return (
    <Box
      component={RouterLink}
      to={href}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
        px: 2,
        textDecoration: "none",
        color: "inherit",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Typography fontSize={13}>{space.name}</Typography>
      <Typography fontSize={13} fontWeight="bold">
        {count.toLocaleString()}
      </Typography>
    </Box>
  );
}

// Popover content showing per-space breakdown
function SpaceBreakdownContent({
  spaces,
  filter,
  buildHref,
  totalSpaces,
  open,
}: {
  spaces: SpaceInfo[];
  filter: Record<string, unknown>;
  buildHref: (spaceId: string) => string;
  totalSpaces: number;
  open: boolean;
}) {
  const remaining = totalSpaces - spaces.length;
  return (
    <Box sx={{ py: 1, minWidth: 240 }}>
      {spaces.map((space) => (
        <SpaceCountRow
          key={space.id}
          space={space}
          filter={filter}
          href={buildHref(space.id)}
          skip={!open}
        />
      ))}
      {remaining > 0 && (
        <Typography
          fontSize={12}
          color="text.secondary"
          sx={{ px: 2, py: 0.75 }}
        >
          and {remaining} more space{remaining > 1 ? "s" : ""}…
        </Typography>
      )}
    </Box>
  );
}

// Wrapper that shows a popover on hover
function HoverableBoomStat({
  spaces,
  filter,
  buildHref,
  orgCount,
  totalSpaces,
  children,
}: {
  spaces: SpaceInfo[];
  filter: Record<string, unknown>;
  buildHref: (spaceId: string) => string;
  orgCount: number;
  totalSpaces: number;
  children: React.ReactNode;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const open = Boolean(anchorEl) && orgCount > 0;

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (orgCount === 0) return;
    clearTimeout(timeoutRef.current);
    setAnchorEl(e.currentTarget);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setAnchorEl(null), 150);
  };

  const handlePopoverEnter = () => {
    clearTimeout(timeoutRef.current);
  };

  const handlePopoverLeave = () => {
    timeoutRef.current = setTimeout(() => setAnchorEl(null), 150);
  };

  return (
    <Box
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      sx={{ flex: 1, minWidth: "270px", cursor: "default" }}
    >
      {children}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        sx={{ pointerEvents: "none", mt: 0.5 }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverEnter,
            onMouseLeave: handlePopoverLeave,
            sx: { pointerEvents: "auto" },
          },
        }}
      >
        <SpaceBreakdownContent
          spaces={spaces}
          filter={filter}
          buildHref={buildHref}
          totalSpaces={totalSpaces}
          open={open}
        />
      </Popover>
    </Box>
  );
}

export function OrgBoomStats({
  organizationMrn,
  spaceEdges,
  spacesLoading,
  totalSpaces,
}: OrgBoomStatsProps) {
  const spaces: SpaceInfo[] = spaceEdges.map(({ node }) => ({
    id: node.mrn.split("/").pop() || "",
    name: node.name,
    mrn: node.mrn,
  }));

  const criticalAndHigh = useCount(organizationMrn, {
    rating: [ScoreRating.Critical, ScoreRating.High],
  });

  const withExceptions = useCount(organizationMrn, {
    hasExceptions: true,
  });

  const { mttrSla, isLoading: slaLoading } = useMetrics({
    entityMrn: organizationMrn,
    metricMrns: [Metrics.MTTRSla],
  });

  const overdueSlaCount = useMemo(
    () => mttrSla.reduce((sum, sla) => sum + sla.pastDate, 0),
    [mttrSla],
  );
  const atRiskSlaCount = useMemo(
    () => mttrSla.reduce((sum, sla) => sum + sla.nearDate, 0),
    [mttrSla],
  );

  const isLoading =
    spacesLoading ||
    criticalAndHigh.loading ||
    withExceptions.loading ||
    slaLoading;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <HoverableBoomStat
        spaces={spaces}
        filter={{ rating: [ScoreRating.Critical, ScoreRating.High] }}
        orgCount={criticalAndHigh.count}
        totalSpaces={totalSpaces}
        buildHref={(spaceId) =>
          `/space/findings/cves?spaceId=${spaceId}&riskRating=Critical&riskRating=High`
        }
      >
        <BoomStatCard
          variant="simple"
          value={criticalAndHigh.count}
          label="Critical & High Findings"
        />
      </HoverableBoomStat>

      <HoverableBoomStat
        spaces={spaces}
        filter={{ hasExceptions: true }}
        orgCount={withExceptions.count}
        totalSpaces={totalSpaces}
        buildHref={(spaceId) => `/space/findings/exceptions?spaceId=${spaceId}`}
      >
        <BoomStatCard
          variant="simple"
          value={withExceptions.count}
          label="Findings With Exceptions"
        />
      </HoverableBoomStat>

      <HoverableBoomStat
        spaces={spaces}
        filter={{ hasOverdueSla: true }}
        orgCount={overdueSlaCount}
        totalSpaces={totalSpaces}
        buildHref={(spaceId) => `/space/dashboard/slas?spaceId=${spaceId}`}
      >
        <BoomStatCard
          variant="simple"
          value={overdueSlaCount}
          label="Overdue SLA Findings"
        />
      </HoverableBoomStat>

      <HoverableBoomStat
        spaces={spaces}
        filter={{ hasAtRiskSla: true }}
        orgCount={atRiskSlaCount}
        totalSpaces={totalSpaces}
        buildHref={(spaceId) => `/space/dashboard/slas?spaceId=${spaceId}`}
      >
        <BoomStatCard
          variant="simple"
          value={atRiskSlaCount}
          label="At Risk SLA Findings"
        />
      </HoverableBoomStat>
    </Box>
  );
}
